import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatINR } from "@/data/products";
import OrderTrackingTimeline from "@/components/OrderTrackingTimeline";

const ALLOWED_TRANSITIONS = {
  created: ["paid", "failed", "cancelled"],
  paid: ["processing", "cancelled"],
  processing: ["shipped", "cancelled"],
  shipped: ["delivered"],
  delivered: [],
  cancelled: [],
  failed: [],
};

const OrderDetail = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [updating, setUpdating] = useState(false);
  const [shipping, setShipping] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-order", id],
    queryFn: async () => (await api.get(`/admin/orders/${id}`)).data,
  });

  const updateStatus = async (status) => {
    setUpdating(true);
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      queryClient.invalidateQueries({ queryKey: ["admin-order", id] });
      toast.success(`Order marked ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not update status");
    } finally {
      setUpdating(false);
    }
  };

  const shipViaShiprocket = async () => {
    setShipping(true);
    try {
      await api.post(`/admin/orders/${id}/ship`);
      queryClient.invalidateQueries({ queryKey: ["admin-order", id] });
      toast.success("Shipment created");
    } catch (err) {
      queryClient.invalidateQueries({ queryKey: ["admin-order", id] });
      toast.error(err.response?.data?.message || "Could not create shipment");
    } finally {
      setShipping(false);
    }
  };

  if (isLoading || !data) return null;
  const order = data.order;
  const nextStatuses = ALLOWED_TRANSITIONS[order.status] || [];

  return (
    <div className="max-w-3xl">
      <Link to="/admin/orders" className="text-xs text-[#5C5C5C] hover:text-[#111111]">&larr; Back to orders</Link>
      <h1 className="font-serif-lux text-3xl mt-4 mb-8">{order.orderNumber}</h1>

      <div className="grid md:grid-cols-2 gap-10 mb-10">
        <div>
          <h2 className="text-[11px] tracking-[0.25em] uppercase text-[#5C5C5C] mb-3">Customer</h2>
          <p className="text-sm">{order.user?.name}</p>
          <p className="text-sm text-[#5C5C5C]">{order.user?.email}</p>

          <h2 className="text-[11px] tracking-[0.25em] uppercase text-[#5C5C5C] mb-3 mt-6">Shipping Address</h2>
          <p className="text-sm">{order.shippingAddress?.fullName}</p>
          <p className="text-sm text-[#5C5C5C]">{order.shippingAddress?.line1}{order.shippingAddress?.line2 ? `, ${order.shippingAddress.line2}` : ""}</p>
          <p className="text-sm text-[#5C5C5C]">{order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.postalCode}</p>
          <p className="text-sm text-[#5C5C5C]">{order.shippingAddress?.phone}</p>
        </div>

        <div>
          <h2 className="text-[11px] tracking-[0.25em] uppercase text-[#5C5C5C] mb-3">Status</h2>
          <p className="text-sm capitalize mb-4">{order.status} · Payment: {order.payment?.status}</p>

          {order.status === "paid" && (
            <button
              onClick={shipViaShiprocket}
              disabled={shipping}
              className="mb-4 bg-[#111111] text-white rounded-full px-5 py-3 text-xs uppercase tracking-wide hover:bg-[#C07C5F] transition-colors disabled:opacity-50"
            >
              {shipping ? "Booking with Shiprocket…" : "Ship via Shiprocket"}
            </button>
          )}

          {nextStatuses.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {nextStatuses.map((s) => (
                <button
                  key={s}
                  disabled={updating}
                  onClick={() => updateStatus(s)}
                  className="border border-[#111111] rounded-full px-4 py-2 text-xs uppercase tracking-wide hover:bg-[#111111] hover:text-white transition-colors disabled:opacity-50"
                >
                  Mark {s}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#5C5C5C]">No further transitions.</p>
          )}

          {order.shipping?.awb_code && (
            <div className="mt-6 border border-[#EBE8E3] rounded-lg p-4 bg-[#F9F8F6]">
              <h3 className="text-[10px] tracking-[0.25em] uppercase text-[#5C5C5C] mb-2">Shipment</h3>
              <OrderTrackingTimeline order={order} queryKey={["admin-order", id]} />
            </div>
          )}
        </div>
      </div>

      <h2 className="text-[11px] tracking-[0.25em] uppercase text-[#5C5C5C] mb-3">Items</h2>
      <div className="border border-[#EBE8E3] rounded-lg divide-y divide-[#EBE8E3] bg-white mb-6">
        {order.items.map((item, i) => (
          <div key={i} className="flex justify-between px-5 py-3 text-sm">
            <span>{item.name} × {item.qty}</span>
            <span>{formatINR((item.kind === "box" ? item.boxPrice : item.unitPrice) * item.qty)}</span>
          </div>
        ))}
      </div>

      <div className="flex justify-between text-lg font-serif-lux">
        <span>Total</span>
        <span>{formatINR(order.total)}</span>
      </div>
    </div>
  );
};

export default OrderDetail;
